import { useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FONT, COLOR } from "../lib/tokens";
import { useTheme as useMCMTheme } from "../lib/theme";
import {
  DAY_GRID_HOURS,
  bucketDayByHour,
  fmtHourLabel,
  fmtTime,
  weekDays,
  weekDayShorts,
  fmtWeekHeader,
  isSameDay,
  startOfDay,
} from "../lib/calendarUtils";
import { getMeasurementSystem } from "../lib/unitPrefs";

// CalendarWeek — 7-column grid, one column per day, hour rows down.
// Tap a column header → drill into Day view for that date.
// Tap a meal tile → MealDetailDrawer (parent handles).

const HOUR_ROW_HEIGHT = 44;

export default function CalendarWeek({
  cursorDate,
  meals = [],
  pastCooks = [],
  pantry = [],
  userId,
  nameFor,
  findRecipe,
  findUserRecipe,
  onPrevWeek,
  onNextWeek,
  onSelectDay,
  onTapMeal,
  onTapCook,
}) {
  const { theme } = useMCMTheme();
  const system = getMeasurementSystem();
  const today = useMemo(() => startOfDay(new Date()), []);
  const days = useMemo(() => weekDays(cursorDate, system), [cursorDate, system]);
  const shorts = useMemo(() => weekDayShorts(system), [system]);

  // One bucket per day. Compute once.
  const dayBuckets = useMemo(
    () => days.map(d => bucketDayByHour(d, meals, pastCooks)),
    [days, meals, pastCooks],
  );

  // Auto-scroll to 8 AM-ish on mount / week change.
  const gridRef = useRef(null);
  useEffect(() => {
    if (!gridRef.current) return;
    let target = 8;
    for (const h of DAY_GRID_HOURS) {
      const any = dayBuckets.some(b => (b.get(h) || []).length > 0);
      if (any) { target = h; break; }
    }
    const offset = (target - DAY_GRID_HOURS[0]) * HOUR_ROW_HEIGHT;
    gridRef.current.scrollTop = Math.max(0, offset - 8);
  }, [cursorDate]); // re-scroll on week navigation

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 200px)", maxHeight: 720 }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 4px 12px", gap: 8,
      }}>
        <button onClick={onPrevWeek} aria-label="Previous week"
          style={navBtnStyle(theme)}>
          ‹
        </button>
        <div style={{
          fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
          fontSize: 22, color: theme.color.skyInk || COLOR.ink,
          textAlign: "center", flex: 1,
        }}>
          {fmtWeekHeader(cursorDate, system)}
        </div>
        <button onClick={onNextWeek} aria-label="Next week"
          style={navBtnStyle(theme)}>
          ›
        </button>
      </div>

      {/* Day-of-week column headers — sticky-ish above the hour grid.
          glassFillHeavy is translucent, so without the backdrop blur the
          page bleeds through and the row reads as see-through. Match the
          frosted treatment every other calendar surface uses. */}
      <div style={{
        display: "grid",
        gridTemplateColumns: `48px repeat(7, 1fr)`,
        gap: 0,
        background: theme.color.glassFillHeavy,
        backdropFilter: "blur(20px) saturate(140%)",
        WebkitBackdropFilter: "blur(20px) saturate(140%)",
        border: `1px solid ${theme.color.glassBorder}`,
        borderRadius: "16px 16px 0 0",
        padding: "8px 4px",
      }}>
        <div /> {/* spacer over hour rail */}
        {days.map((d, i) => {
          const isToday = isSameDay(d, today);
          return (
            <button
              key={i}
              onClick={() => onSelectDay?.(d)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                gap: 2, padding: "4px 0",
                background: isToday ? COLOR.goldDeep : "transparent",
                border: isToday ? `2px solid ${COLOR.gold}` : "1px solid transparent",
                borderRadius: 10,
                cursor: "pointer",
              }}
            >
              <div style={{
                fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
                color: isToday ? COLOR.gold : (theme.color.skyInkMuted || COLOR.muted),
                letterSpacing: "0.16em",
              }}>
                {shorts[i]}
              </div>
              <div style={{
                fontFamily: FONT.forma, fontSize: 14, fontWeight: 700,
                color: isToday ? COLOR.gold : (theme.color.skyInk || COLOR.ink),
                lineHeight: 1,
              }}>
                {d.getDate()}
              </div>
            </button>
          );
        })}
      </div>

      {/* Hour grid */}
      <div ref={gridRef} style={{
        flex: 1, overflowY: "auto", overflowX: "hidden",
        background: theme.color.glassFillHeavy,
        borderLeft: `1px solid ${theme.color.glassBorder}`,
        borderRight: `1px solid ${theme.color.glassBorder}`,
        borderBottom: `1px solid ${theme.color.glassBorder}`,
        borderRadius: "0 0 16px 16px",
        boxShadow: theme.shadow.glass,
      }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={cursorDate.toDateString()}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
          >
            {DAY_GRID_HOURS.map(h => (
              <div key={h} style={{
                display: "grid",
                gridTemplateColumns: `48px repeat(7, 1fr)`,
                minHeight: HOUR_ROW_HEIGHT,
                borderBottom: `1px solid ${theme.color.glassBorder}`,
              }}>
                {/* Hour rail label */}
                <div style={{
                  padding: "6px 6px 0",
                  fontFamily: FONT.forma, fontSize: 9, fontWeight: 500,
                  color: theme.color.skyInkMuted || COLOR.muted,
                  letterSpacing: "0.06em",
                  textAlign: "right",
                }}>
                  {fmtHourLabel(h)}
                </div>
                {days.map((d, i) => {
                  const entries = dayBuckets[i].get(h) || [];
                  const isToday = isSameDay(d, today);
                  return (
                    <div key={i} style={{
                      borderLeft: `1px solid ${theme.color.glassBorder}`,
                      padding: 2,
                      background: isToday ? "rgba(212,166,55,0.04)" : "transparent",
                      display: "flex", flexDirection: "column", gap: 2,
                    }}>
                      {entries.map(({ kind, row, time }) => kind === "cook" ? (
                        <CompactCookTile
                          key={`cook-${row.id}`}
                          cook={row}
                          time={time}
                          onClick={() => onTapCook?.(row.id)}
                        />
                      ) : (
                        <CompactMealTile
                          key={`meal-${row.id}`}
                          meal={row}
                          time={time}
                          findRecipe={findRecipe}
                          findUserRecipe={findUserRecipe}
                          pantry={pantry}
                          onClick={() => onTapMeal?.(row)}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function CompactMealTile({ meal, time, findRecipe, findUserRecipe, pantry, onClick }) {
  const recipe = meal.recipe_slug ? findRecipe?.(meal.recipe_slug, findUserRecipe, meal.recipe_user_id) : null;
  const pantryEatRow = meal.from_pantry_row_id
    ? (pantry || []).find(p => p.id === meal.from_pantry_row_id)
    : null;
  const isRequest = meal.cook_id == null && !meal.from_pantry_row_id;
  const emoji = recipe?.emoji || pantryEatRow?.emoji || "🍽️";
  const title = recipe?.title || pantryEatRow?.name || meal.recipe_slug || "?";

  return (
    <button
      onClick={onClick}
      title={`${title} · ${fmtTime(time)}`}
      style={{
        display: "flex", alignItems: "center", gap: 4,
        padding: "4px 5px", width: "100%",
        background: isRequest ? "rgba(212,166,55,0.18)" : "rgba(255,255,255,0.55)",
        border: `1px solid ${isRequest ? "rgba(212,166,55,0.45)" : "rgba(255,255,255,0.7)"}`,
        borderRadius: 6,
        cursor: "pointer",
        minHeight: 26,
      }}
    >
      <span style={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>{emoji}</span>
      <span style={{
        fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
        fontSize: 11, color: COLOR.ink,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        lineHeight: 1.1, minWidth: 0, flex: 1, textAlign: "left",
      }}>
        {title}
      </span>
    </button>
  );
}

function CompactCookTile({ cook, time, onClick }) {
  return (
    <button
      onClick={onClick}
      title={`${cook.recipe_title} · cooked ${fmtTime(time)}`}
      style={{
        display: "flex", alignItems: "center", gap: 4,
        padding: "4px 5px", width: "100%",
        background: "rgba(180,200,160,0.25)",
        border: "1px solid rgba(180,200,160,0.45)",
        borderRadius: 6,
        cursor: "pointer",
        opacity: 0.6,
        minHeight: 26,
      }}
    >
      <span style={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>{cook.recipe_emoji || "🍽️"}</span>
      <span style={{
        fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
        fontSize: 11, color: COLOR.ink,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        lineHeight: 1.1, minWidth: 0, flex: 1, textAlign: "left",
      }}>
        ✓ {cook.recipe_title}
      </span>
    </button>
  );
}

function navBtnStyle(theme) {
  return {
    width: 36, height: 36, flexShrink: 0,
    borderRadius: 999,
    border: `1px solid ${theme.color.glassBorder}`,
    background: theme.color.glassFillHeavy,
    color: theme.color.skyInk || COLOR.ink,
    fontFamily: FONT.forma, fontSize: 20, fontWeight: 500,
    cursor: "pointer", padding: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    boxShadow: theme.shadow.soft,
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  };
}
